import { DocumentType, modelOptions, mongoose, PropType, post, pre, prop, Severity } from "@typegoose/typegoose";
import { z } from "zod";
import {
    DeviceTypeString,
    getZodSchemaFromClass,
    type NodeId,
    NodeIdString,
    ObjectIdString,
    validate,
} from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import { ProductionJigLogFieldsWithDefaults, ProductionJigLogSchemaTypes } from "../types/productionJigLog.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "ProductionJigLog" as const;

/**
 * This model was requested by the hardware team and is responsible for tracking how devices did in their quality
 * control checks.
 */
@modelOptions({
    schemaOptions: {
        collection: "ProductionJigLogs",
        timestamps: true,
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
    },
})
@post<SchemaBase<ProductionJigLog>>(...addSchemaKeyHook(SCHEMA_KEY))
@post<ProductionJigLog>("init", function (this: DocumentType<ProductionJigLog>) {
    // Store original values in $locals for comparison in pre-save hook
    // $locals is Mongoose's recommended property for temporary document-level data
    if (this.tests) {
        this.$locals.originalTests = JSON.stringify(this.tests);
    }
    if (this.processes) {
        this.$locals.originalProcesses = JSON.stringify(this.processes);
    }
})
@post<ProductionJigLog>(["find", "findOne", "findOneAndUpdate"], async function (this, doc) {
    // Handle find results (doc is the result, may be array for find)
    if (Array.isArray(doc)) {
        for (const item of doc) {
            if (item?.$locals) {
                if (item.tests) {
                    item.$locals.originalTests = JSON.stringify(item.tests);
                }
                if (item.processes) {
                    item.$locals.originalProcesses = JSON.stringify(item.processes);
                }
            }
        }
    } else if (doc?.$locals) {
        if (doc.tests) {
            doc.$locals.originalTests = JSON.stringify(doc.tests);
        }
        if (doc.processes) {
            doc.$locals.originalProcesses = JSON.stringify(doc.processes);
        }
    }
})
@pre<ProductionJigLog>("save", async function (this: DocumentType<ProductionJigLog>) {
    // Mark mixed fields as modified if they've changed
    const originalTests = this.$locals?.originalTests;
    const originalProcesses = this.$locals?.originalProcesses;

    // If this is a new document, mark as modified
    if (this.isNew) {
        if (this.tests) this.markModified("tests");
        if (this.processes) this.markModified("processes");
        return;
    }

    // If baseline exists and current value differs, mark as modified
    if (originalTests !== undefined && JSON.stringify(this.tests) !== originalTests) {
        this.markModified("tests");
    }
    if (originalProcesses !== undefined && JSON.stringify(this.processes) !== originalProcesses) {
        this.markModified("processes");
    }
})
@post<ProductionJigLog>("save", function (this: DocumentType<ProductionJigLog>) {
    // Update baselines after successful save to prevent unnecessary markModified on subsequent saves
    const testsWasLoaded = this.$locals.originalTests !== undefined;
    const testsIsSet = "tests" in this && this.tests !== undefined;

    if (testsWasLoaded || testsIsSet) {
        if (this.tests) {
            this.$locals.originalTests = JSON.stringify(this.tests);
        } else {
            this.$locals.originalTests = undefined;
        }
    }

    const processesWasLoaded = this.$locals.originalProcesses !== undefined;
    const processesIsSet = "processes" in this && this.processes !== undefined;

    if (processesWasLoaded || processesIsSet) {
        if (this.processes) {
            this.$locals.originalProcesses = JSON.stringify(this.processes);
        } else {
            this.$locals.originalProcesses = undefined;
        }
    }
})
export class ProductionJigLog extends SchemaBase<ProductionJigLog, ProductionJigLogFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: NodeIdString.optional() })
    @prop({ type: () => String, required: false })
    public node_id?: NodeId;

    @validate({ zodValidator: z.coerce.date() })
    @prop({ type: () => Date, required: true })
    public date!: Date;

    @validate({ zodValidator: z.record(z.string(), z.any()).optional() })
    @prop({ type: () => Object, required: false, allowMixed: Severity.ALLOW })
    public tests?: Record<string, any>;

    @validate({ zodValidator: z.record(z.string(), z.any()) })
    @prop({ type: () => Object, required: true, allowMixed: Severity.ALLOW })
    public processes!: Record<string, any>;

    @validate({ zodValidator: z.array(z.string()) })
    @prop({ type: [String], required: true, default: [] }, PropType.ARRAY)
    public logs: string[] = [];

    @validate({ zodValidator: DeviceTypeString })
    @prop({ type: () => String, required: true })
    public deviceType!: string;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public runTime?: number;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public jigID?: string;

    static getters = {
        /**
         * Returns the logs as a formatted string
         */
        getFormattedLogs: (self: ProductionJigLog): string => self.logs.join("\n"),

        /**
         * Returns the number of logs
         */
        getLogCount: (self: ProductionJigLog): number => self.logs.length,

        /**
         * Returns the runtime in a human-readable format
         */
        getFormattedRunTime: (self: ProductionJigLog): string => {
            if (self.runTime === undefined) {
                return "N/A";
            }

            // Format seconds into minutes and seconds
            const minutes = Math.floor(self.runTime / 60);
            const seconds = Math.floor(self.runTime % 60);

            if (minutes > 0) {
                return `${minutes}m ${seconds}s`;
            } else {
                return `${seconds}s`;
            }
        },
    } as const satisfies Record<string, (self: ProductionJigLog) => any>;

    static utilities = {
        /**
         * Adds a new log entry
         */
        addLogEntry: (self: ProductionJigLog, logEntry: string): ProductionJigLog => ({
            ...self,
            logs: [...self.logs, logEntry],
        }),

        /**
         * Checks if a specific test passed
         */
        didTestPass: (self: ProductionJigLog, testName: string): boolean => {
            if (!self.tests || !self.tests[testName]) {
                return false;
            }

            // Assuming tests have a 'passed' property
            return !!self.tests[testName].passed;
        },
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<ProductionJigLogSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<ProductionJigLog, ProductionJigLogFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

ProductionJigLog.dtoValidators = getZodSchemaFromClass(
    ProductionJigLog,
) as unknown as DtoValidatorTypes<ProductionJigLogSchemaTypes>;
